import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import { Flex } from "@chakra-ui/react";
import { ContentGroup } from "@/components/ContentGroup/ContentGroup";
import { ContentMissing } from "@/components/ContentMissing/ContentMissing";
import { LayoutInfo } from "@/components/LayoutInfo/LayoutInfo";
import { useFeatureEnabled } from "@/services/Features";
import type { FundDetailLayoutComponentMap, FundDetailLayoutComponentProps } from "@/types/fundDetailLayout";

type LayoutHandlerProps = {
	componentMap: FundDetailLayoutComponentMap;
	componentProps: FundDetailLayoutComponentProps;
	items?: LayoutComponentConfig[];
	nestingLevel?: number;
	validateComponent?: (config: LayoutComponentConfig) => boolean;
};

export type LayoutComponentConfig = {
	component?: string;
	items?: LayoutComponentConfig[];
	type?: string;
	types?: string[];
	layout?: string | null;
	flex?: number;
	flexDirection?: "column" | "row";
	width?: string;
	textId?: string;
	disclaimer?: string;
};

export const LayoutHandler = ({
	children,
	items,
	componentMap,
	componentProps,
	validateComponent
}: PropsWithChildren<LayoutHandlerProps>) => {
	const isShelfEnabled = useFeatureEnabled(["collections", "compare"], "any");

	const generatedComponents = useMemo(() => {
		if (!items) {
			return null;
		}

		const filteredItems =
			componentProps?.language === "debug"
				? items
				: toSizedItems(toCompactedItems(items, componentMap, validateComponent), componentMap);

		return renderLayout({
			items: filteredItems,
			componentMap,
			componentProps,
			nestingLevel: 0
		});
	}, [items, componentProps, componentMap, validateComponent]);

	return (
		<Flex flex={1} flexDirection="column" paddingBottom={isShelfEnabled ? "60px" : 0}>
			{generatedComponents}
			{children}
		</Flex>
	);
};

const renderLayout = ({ items, componentMap, componentProps, nestingLevel = 0 }: LayoutHandlerProps) => {
	if (nestingLevel === 0) {
		// console.log(`[LAYOUT START]`);
		nestingLevel++;
	}

	if (!items) {
		// console.log(`${new Array(nestingLevel).fill("--").join("")}[null]`);
		return null;
	}

	return (
		<>
			{items.map(({ component, items: childItems, width = "100%", flex, ...rest }, itemIndex) => {
				const childKey = `component_${itemIndex}`;

				if (!component) {
					// console.log(
					// 	`${new Array(nestingLevel).fill("--").join("")}[${childItems?.map((tmpItem) => tmpItem.component)}]`,
					// 	componentProps
					// );

					// This is a group of items, wrap it in a container

					return (
						<LayoutInfo key={childKey} type="group" name="group">
							<ContentGroup
								hideIfEmpty={true}
								shouldApplySmallScreenPadding={nestingLevel < 2}
								{...(nestingLevel >= 2
									? {
											flexDirection: "column",
											borderTop: "none",
											paddingTop: 0
										}
									: {})}
							>
								{renderLayout({
									items: childItems,
									componentMap,
									componentProps,
									nestingLevel: nestingLevel + 1
								})}
							</ContentGroup>
						</LayoutInfo>
					);
				}

				const ComponentFunction = componentMap[component];
				// Component is defined but not required for current layout
				if (ComponentFunction === null) {
					// console.log(`${new Array(nestingLevel).fill("--").join("")}[ComponentFunction==null]`);
					return null;
				}

				// Component has not been defined
				if (ComponentFunction === undefined) {
					// console.log(`${new Array(nestingLevel).fill("--").join("")}[MISSING: "${component}"]`);
					if (nestingLevel === 1) {
						return (
							<LayoutInfo key={childKey} type="component" name={component}>
								<ContentGroup hideIfEmpty={true} borderTop="1px solid #E2E2E2" paddingY={8}>
									<ContentMissing component={component} />
								</ContentGroup>
							</LayoutInfo>
						);
					} else {
						return (
							<LayoutInfo key={childKey} type="component" name={component}>
								<ContentMissing component={component} />
							</LayoutInfo>
						);
					}
				}

				// console.log(`${new Array(nestingLevel).fill("--").join("")}[${component}]`);
				const completeProps = {
					...componentProps,
					config: rest,
					flex: flex
				};

				// console.log(`${new Array(nestingLevel).fill("--").join("")}[${component}], flex: ${flex} layout: ${layout}`);

				if (nestingLevel === 1 && dontWrapList.indexOf(component) === -1) {
					return (
						<LayoutInfo key={childKey} type="component" name={component}>
							<ContentGroup hideIfEmpty={true}>
								<ComponentFunction {...completeProps} />
							</ContentGroup>
						</LayoutInfo>
					);
				} else {
					// I know this is bad but it will do for now
					if (component === "fxProfileHeroPanel") {
						return (
							<LayoutInfo key={childKey} type="component" name={component}>
								<ComponentFunction {...completeProps} />
							</LayoutInfo>
						);
					}
					return (
						<LayoutInfo key={childKey} type="component" name={component}>
							<Flex
								flexDirection="column"
								flex={1}
								maxWidth={{
									md: width
								}}
							>
								<ComponentFunction {...completeProps} />
							</Flex>
						</LayoutInfo>
					);
				}
			})}
		</>
	);
};

const dontWrapList = ["fxCommentaryPanel", "fxProfileHeroPanel", "fxBreakdownPanel"];

const toCompactedItems = (
	items: LayoutComponentConfig[],
	componentMap: FundDetailLayoutComponentMap,
	validateComponent: ((config: LayoutComponentConfig) => boolean) | undefined
): LayoutComponentConfig[] => {
	return items
		.filter((item) => {
			const { component } = item;

			if (component && !componentMap[component]) {
				return false;
			}

			return validateComponent ? validateComponent(item) : true;
		})
		.map(({ component, items, ...rest }) => {
			const compactedItems = items ? toCompactedItems(items, componentMap, validateComponent) : undefined;

			if (!component && Array.isArray(compactedItems) && compactedItems.length === 1) {
				return compactedItems[0];
			}
			if (!component && Array.isArray(compactedItems) && compactedItems.length === 0) {
				return null;
			}

			return {
				component,
				...(compactedItems
					? {
							items: compactedItems
						}
					: {}),
				...rest
			};
		})
		.filter((item) => {
			return !!item;
		}) as LayoutComponentConfig[];
};

const toSizedItems = (
	items: LayoutComponentConfig[],
	componentMap: FundDetailLayoutComponentMap,
	nestingLevel: number = 0
): LayoutComponentConfig[] => {
	return items.map((row) => {
		const { items, flexDirection } = row;

		if (items) {
			const itemCount = items.length;
			const filteredItems = items.filter(({ items }) => {
				return !Array.isArray(items) || items.length > 0;
			});

			return {
				...row,
				items: toSizedItems(filteredItems, componentMap, nestingLevel + 1).map((item) => {
					return {
						...item,
						width: nestingLevel + 1 >= 2 ? "100%" : `${100 / itemCount}%`
					};
				})
			};
		}

		return row;
	});
};

export const getComponentConfigs = (items: LayoutComponentConfig[], tree: LayoutComponentConfig[] = []): LayoutComponentConfig[] => {
	if (!items) {
		return [];
	}

	for (const item of items) {
		if (item.component) {
			tree.push(item);
		}
		if (item.items) {
			tree = tree.concat(getComponentConfigs(item.items));
		}
	}

	return tree;
};
